/**
 * 
 * @param str // "Some test {{RES}}"
 * @param values // { RES: "value" }
 * @name parseTemplate
 * @returns 
 */

import { T_KeyValue } from "@/types/app.type";
import { isEmptyObject } from "./utils.helper";



export const parseTemplate = async (str: string = "noStr", values: Record<string, string>): Promise<string> => {
    // console.log("parseTemplate -------- ", str, values);
    const filteredStr: string = str && typeof (str) === "string" && !isEmptyObject(values) ? str?.replace(/{{(.*?)}}/g, (_, key) => {
        // console.log("parseTemplate 2 -------- ", str, key, values, values[key.trim()]);
        return values?.[key.trim()] || "";
    }) : str || "";
    return (filteredStr === "noStr" ? "" : filteredStr);
}

export const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

export const flattenJson = async (obj: any, parentKey = '', result: any = {}): Promise<Record<string, any>> => {
    for (const key in obj) {
        if (!obj.hasOwnProperty(key)) continue;

        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        const value = obj[key];

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            await flattenJson(value, fullKey, result);
        } else {
            result[fullKey] = value;
        }
    }
    return result;
}

function resolveVal(val: string, data: any): any {
    // Case 1: plain path (no template)
    if (!val.includes("[") && !val.includes("]")) {
        return getNestedValue(data, val);
    }

    // Case 2: template string. [path1]-[path2]_[path3]staticText
    return val.replace(/\[([^\]]+)\]/g, (_, path) => { // match content inside []
        const value = getNestedValue(data, path.trim());
        return value !== undefined ? String(value) : "";
    });
}

const mergePayload = (payload: any, preDefined: any): any => {
    // Start with all keys from both payload and preDefined
    const result: any = {};
    const allKeys = new Set([...Object.keys(payload || {}), ...Object.keys(preDefined || {})]);
 
    for (const key of allKeys) {
        const payloadValue = payload?.[key];
        const predefinedValue = preDefined?.[key];
 
        // If preDefined has the key, check if it's an object (non-array)
        if (predefinedValue !== undefined) {
            // If preDefined value is an object (non-array), recursively merge
            if (
                typeof predefinedValue === "object" &&
                predefinedValue !== null &&
                !Array.isArray(predefinedValue)
            ) {
                // If payload value is also an object (non-array), merge them recursively
                if (
                    typeof payloadValue === "object" &&
                    payloadValue !== null &&
                    !Array.isArray(payloadValue)
                ) {
                    result[key] = mergePayload(payloadValue, predefinedValue);
                } else {
                    // Payload is not an object, preDefined is object - use preDefined
                    result[key] = predefinedValue;
                }
            } else {
                // preDefined value is NOT an object (primitive or array) - use preDefined (priority)
                result[key] = predefinedValue;
            }
        } else if (payloadValue !== undefined) {
            // preDefined doesn't have this key, use payload value
            // If payload value is an object, still need to process it
            if (
                typeof payloadValue === "object" &&
                payloadValue !== null &&
                !Array.isArray(payloadValue)
            ) {
                result[key] = mergePayload(payloadValue, {});
            } else {
                result[key] = payloadValue;
            }
        }
    }
 
    return result;
}

/**
 * Recursively maps flat data to a nested payload structure based on a mapping configuration.
 * 
 * @name mapNestedJson
 * @description 
 * The function transforms flat data into a nested structure following the mapping rules defined in `obj`.
 * It supports multiple mapping patterns including direct key mapping, string concatenation, template resolution,
 * and recursive object traversal.
 * 
 * @param obj - Mapping configuration that can be:
 *   - A string: Direct key reference to flat data (e.g., "firstName")
 *   - An object with "concat": Concatenates multiple flat keys (e.g., { concat: ["code", "num"], separator: "-" })
 *   - An object with "source" and "val": Template resolution with bracket notation (e.g., {"exp": 43200, "val": "[json.data.reservation_id]", "source": "res"})
 *   - A nested object: Recursively processes each property
 * 
 * @param data - Flat source data to extract values from
 * @param output - Accumulator object for building the nested result (default: {})
 * 
 * @returns The transformed nested structure with values from the source data
 * 
 * 
 * @example
 * // Nested object mapping
 * const mapping = {
 * {
 * "email": "email",
 * "language": "language",
 * "firstName": "fullName",
 * "customProperties": {
 *      "notes": "userId",
 *      "newsletterSubscription": "newsletterConsent",
 *      "termsConsent": {
 *          "consent": "newsletterConsent"
 *      }
 * }
 * const data = { fullName: "Ravi", email: "ravi@example.com", language: "en", userId: "12345", newsletterConsent: true };
 * mapNestedJson(mapping, data)
 * // Returns: { email: "ravi@example.com", language: "en", firstName: "Ravi", customProperties: { notes: "12345", newsletterSubscription: true, termsConsent: { consent: true } } }
 */

function mapNestedJson (obj: string | { [key: string]: T_KeyValue | undefined }, data:any, output: T_KeyValue = {}): any {

    if(typeof obj === "string"){
        return data?.[obj];
    }else if(obj?.hasOwnProperty("concat") && Array.isArray(obj["concat"])){
    // phone: { concat: ["countryCode", "phoneNo"], separator: "-" }
        const concatedValue = ((arr = [], delim = "") => {
            let d = "";
            let str = "";
            obj["concat"].forEach((elm: string) => {
                str += d + (data?.hasOwnProperty(elm) ? data[elm] : elm);
                d = delim;
            });
            return str;
        })(obj["concat"], obj["separator"]);
        
        return concatedValue;
    }else if (obj?.hasOwnProperty("source") && obj?.val) {
        return resolveVal(obj.val, data);
    }else if(obj?.hasOwnProperty("fieldName") && obj?.hasOwnProperty("options")){
        return data?.[obj.fieldName] && obj.options?.[data[obj.fieldName]] ? obj.options[data[obj.fieldName]] : data?.[obj.fieldName];
    }else if(typeof obj === "object"){
        for(const key in obj){
            output[key] = mapNestedJson(obj?.[key], data);
        }
        return output;
    }
}

export const parseApiInput = async (data: any, payloadStruct: any) => {
    const CRLFregex = /(\r\n|\n|\r)/gm;
    // payloadStruct -- comming from appConfig
    // data -- comming from form

    const newPayload = mapNestedJson(payloadStruct?.map, data);

    const payload: T_KeyValue = {};

    // {
    //   "marketingConsent": {
    //       "id": "marketing.email",
    //       "consent": "marketingConsent"
    //   }
    // }

    /**
     * API payload mapping with form keys
     * in PayloadStruct?.map Key is the API payload key and value is the Form JSON Key.
     * payload = API payload
     * data = Form data with structure
     */
    // for (const key in payloadStruct?.map) {
    //     if (typeof (payloadStruct?.map[key]) === "string") {
    //         // payload[key] = ["message", "note"].includes(payloadStruct?.map[key]) ? String(data[payloadStruct?.map[key]])?.replace(CRLFregex, '') : data[payloadStruct?.map[key]];
    //         payload[key] = data[payloadStruct?.map[key]];
    //     } else if (typeof (payloadStruct?.map[key]) === "object") {
    //         if (payloadStruct?.map[key]?.hasOwnProperty("concat")) {
    //             // phone: { concat: ["countryCode", "phoneNo"], separator: "-" }
    //             if (Array.isArray(payloadStruct?.map[key]["concat"])) {
    //                 payload[key] = ((arr = [], delim = "") => {
    //                     let d = "";
    //                     let str = "";
    //                     payloadStruct?.map[key]["concat"].forEach((elm: string) => {
    //                         str += d + (data?.hasOwnProperty(elm) ? data[elm] : elm);
    //                         d = delim;
    //                     });
    //                     return str;
    //                 })(payloadStruct?.map[key]["concat"], payloadStruct?.map[key]["separator"]);
    //             } else if (typeof (payloadStruct?.map[key]["concat"] === "string")) {
    //                 payload[key] = data?.hasOwnProperty(payloadStruct?.map[key]["concat"]) ? data[payloadStruct?.map[key]["concat"]] : payloadStruct?.map[key]["concat"];
    //             }
    //         } else {
    //             payload[key] = ((obj: T_KeyValue) => { //{ "reservation_no": }
    //                 const output: T_KeyValue = {};
    //                 if (obj.hasOwnProperty("source") && obj?.val) {
    //                     return resolveVal(obj.val, data);
    //                 } else {
    //                     for (const subkey in obj) {
    //                         if (data.hasOwnProperty(obj[subkey])) {
    //                             output[subkey] = data[obj[subkey]]
    //                         } else {
    //                             output[subkey] = obj[subkey];
    //                         }
    //                     }
    //                 }
    //                 return output;
    //             })(payloadStruct?.map[key]);
    //         }
    //     }
    // }

    let preDefined: { [key: string]: string | number | boolean } = {};

    for (const key in payloadStruct?.preDefined) {
        // console.log("hello------ ", payloadStruct, payloadStruct?.preDefined, key, payloadStruct?.preDefined?.[key])
        if (["string"]?.includes(typeof (payloadStruct?.preDefined?.[key])) && ((payloadStruct?.preDefined?.[key]) as string)?.includes("getValue:")) {
            const exeAble = payloadStruct?.preDefined?.[key]?.split("getValue:");
            preDefined[key] = eval(exeAble[1]);
        } else {
            preDefined[key] = ((obj: T_KeyValue) => {
                if (obj.hasOwnProperty("source") && obj?.val) {
                    return payloadStruct?.preDefined[key]?.val;
                } else {
                    return payloadStruct?.preDefined[key];
                }
            })(payloadStruct?.preDefined[key]);
        }
    }

    const finalPayload = mergePayload(newPayload, preDefined || {}) //{ ...payload, ...(preDefined || {}) };
    console.log("parseApiInput ----- ", newPayload, preDefined, finalPayload);
    return finalPayload;
};

export const getKeyFromPath = (str: string = "noStr", value: any = {}) => {
    // 1. Find all occurrences of [[...]]
    return str.replace(/\[\[(.*?)\]\]/g, (match, tagContent) => {

        // 2. Parse the parts: [prefix, regexStr, suffix]
        const parts = tagContent.split(',').map((item: string) => item.trim());
        if (parts.length !== 3) return match; // Return original if format is wrong

        const [prefix, regexStr, suffix] = parts;

        // 3. Clean the regex string (remove slashes if user provided them like /d+/g)
        const cleanRegexStr = regexStr.replace(/^\/|\/[gimuy]*$/g, '');

        // 4. Create a dynamic Regex to search the Flat JSON keys
        // We escape the dots in prefix/suffix and wrap the middle in a capture group ()
        const searchPattern = new RegExp(
            `^${prefix.replace(/\./g, '\\.')}\\.(${cleanRegexStr})\\.${suffix.replace(/\./g, '\\.')}$`
        );

        // 5. Search for the key in the flattened JSON
        let foundValue = match; // Default to original tag if not found
        for (const key in value) {
            console.log(key, searchPattern, "pattern for key path");
            const result = key.match(searchPattern);
            if (result && result[1]) {
                foundValue = result[1]; // The captured dynamic key (e.g., 17096)
                break;
            }
        }

        return foundValue;
    });
}

export const apiRequestStoreData = new Map<string, any>();

setInterval(() => {

    apiRequestStoreData.clear();

}, 5 * 60 * 1000); // Run every 5 minutes

function isRequiredCheck(inputValue:any, validationValue:any) {
    if(validationValue === true) {
        if(inputValue === undefined || inputValue === null || inputValue === "") {
            return true; // Validation failed
        }
    }
    return false;
}

export const validationCheck = (data: any, validationConfig: any) => {
    // Implement your validation logic here based on the validationConfig

    const optArr = Object.keys(validationConfig); // {fullName: {req:[true, "name is required"], maxLen:[5, "exceeding max length"]}, email: {req:[true, "email is required"]}, phoneNo: {req:[true, "phoneNo is required"]}}
    for (const key in data) {
      if(optArr?.includes(key)) {
        for(const validationType in validationConfig[key]){
            switch(validationType) {
                case "req":
                    if(isRequiredCheck(data[key], validationConfig[key][validationType][0]))
                        return { isValid: false, error: validationConfig[key][validationType][1] || `${key} is required` };
                    break;
                // Add more validation cases here (e.g. pattern, etc.)
            }
        }
      }
    }

    return { isValid: true, error: "" };
}

export async function fetchAllowedUrl(
    url: string,
    init?: RequestInit
): Promise<Response> {
    const parsedUrl = new URL(url);

    if (parsedUrl.protocol !== "https:") {
        throw new Error("Invalid protocol");
    }

    let targetHostname: string | null = null;
    
    switch (parsedUrl.hostname) {
        case "images.ctfassets.net":
            targetHostname = "images.ctfassets.net";
            break;
        case "videos.ctfassets.net":
            targetHostname = "videos.ctfassets.net";
            break;
        case "brandportal.ingkacentres.com":
            targetHostname = "brandportal.ingkacentres.com";
            break;
    }

    if (!targetHostname) {
        throw new Error("Host not allowed");
    }

    // Explicitly update the url

    const safeUrlString = `https://${targetHostname}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;

    // False positive(SSRF) : hostname is already varified againgst the allowed list, so this is safe to use.
    return fetch(safeUrlString, {...init, redirect: "error"});  //  Don't allow redirects to other hosts
}